///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { numberAttribute } from '@angular/core';
import { cloneDeep, escape, get, isEqual, remove, set } from 'lodash-es';

export const feetMultiplier = 0.3048;
export const squareFeetMultiplier = 0.09290304;

export const convertFeetToMeter = (value: number, fraction = 0) => _round(value * feetMultiplier, fraction);
export const convertMeterToFeet = (value: number, fraction = 0) => _round(value / feetMultiplier, fraction);

export const naturalCompare = (a, b) => {
    const ax = [];
    const bx = [];

    a.replace(/(\d+)|(\D+)/g, (_a, $1, $2) => {
        ax.push([$1 ?? Infinity, $2 ?? '']);
    });
    b.replace(/(\d+)|(\D+)/g, (_, $1, $2) => {
        bx.push([$1 ?? Infinity, $2 ?? '']);
    });

    while (ax.length && bx.length) {
        const an = ax.shift();
        const bn = bx.shift();
        const nn = an[0] - bn[0] || an[1].localeCompare(bn[1]);
        if (nn) {
            return nn;
        }
    }

    return ax.length - bx.length;
};

export const parseFormDataToObj = (obj, formData) => {
    if (!obj || !formData?.controls || !formData?.dirty) {
        return;
    }

    Object.keys(formData.controls)
        .filter((controlKey) => formData.controls[controlKey].dirty)
        .forEach((controlKey) => {
            if (formData.controls[controlKey].controls) {
                if (!obj[controlKey]) {
                    if (Array.isArray(formData.controls[controlKey].controls)) {
                        obj[controlKey] = [];
                    } else {
                        obj[controlKey] = {};
                    }
                }
                parseFormDataToObj(obj[controlKey], formData.controls[controlKey]);
            } else {
                obj[controlKey] = formData.controls[controlKey].value;
            }
        });
};

export const scrollToEl = (scrollContainer, elSelector: string) => {
    scrollContainer.querySelector(elSelector).scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const _cloneDeep = (obj) => {
    return cloneDeep(obj);
};

export const _isEqual = (obj1, obj2): boolean => {
    return isEqual(obj1, obj2);
};

export const _remove = (obj, config): void => {
    remove(obj, config);
};

export const _get = (obj1, obj2) => {
    return get(obj1, obj2);
};

export const _set = (obj1, obj2, obj3) => {
    return set(obj1, obj2, obj3);
};

export const _escape = (obj1: string): string => {
    return escape(obj1);
};

// https://developer.mozilla.org/ru/docs/Web/JavaScript/Reference/Global_Objects/Math/round

export const _round = (value: number, exp: number) => {
    if (typeof exp === 'undefined' || +exp === 0) {
        return Math.round(value);
    }
    value = +value;
    exp = +exp;

    if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
        return NaN;
    }

    let stringValue = (value.toString() + 'e').split('e');
    value = Math.round(+(stringValue[0] + 'e' + (+stringValue[1] + exp)));

    stringValue = (value.toString() + 'e').split('e');
    return +(stringValue[0] + 'e' + (+stringValue[1] - exp));
};

/**
 * Transforms a query param to a number.
 * It a wrapper for numberAttribute transform function @see {@link https://angular.io/api/core/numberAttribute}
 * @param value Value to be transformed.
 *
 *  @usageNotes
 *  ```typescript
 *  @Input({ transform: queryParamToNumber }) param?: number;
 *  ```
 */
export function queryParamToNumber(value: string): number | undefined {
    const transformed = numberAttribute(value);
    return isNaN(transformed) ? undefined : transformed;
}
